import type { Customer, OrganizationModel } from '../api/client/fleet-management'

export const orgMfaStatuses = ['OFF', 'ON_REQUIRED', 'ON_NOT_REQUIRED'] as const
export type OrgMfaStatus = (typeof orgMfaStatuses)[number]

export type OrgPath = Array<{ id: string; name: string }>
export type CustomerWithPath = Customer & { path: OrgPath }

/**
 * Sentinel value for `Customer.parentOrganizationId` indicating the org was
 * surfaced by a sharing relationship rather than the real organization tree.
 *
 * The backend's `getCustomers` query unions in any orgs explicitly shared TO
 * the requested org and sets their `parent_organization_id` to this sentinel
 * so consumers can distinguish a shared org from a real descendant. The value
 * is all-uppercase and will not collide with real org IDs (which are
 * lowercase hex).
 */
export const SHARED_ORG_PARENT_SENTINEL = 'SHARED'

export const isSharedOrg = (org: Pick<Customer, 'parentOrganizationId'>) =>
  org.parentOrganizationId === SHARED_ORG_PARENT_SENTINEL

/**
 * Whether an organization has Billing (products & agreements). The dedicated
 * `hasProductsAndAgreements` flag is authoritative; the legacy fleet billing
 * fields are honored as fallbacks for orgs created before the flag existed.
 */
export const orgHasBilling = (
  org: Pick<OrganizationModel, 'hasProductsAndAgreements' | 'fleetBillingStatus' | 'hasFleetBilling'> | undefined
) => !!org && (!!org.hasProductsAndAgreements || org.fleetBillingStatus === 'ENABLED' || !!org.hasFleetBilling)
